import { useState, useEffect, useRef } from "react";

/**
 * Custom hook for managing booking countdown timers using holdingCardTime (duration in seconds)
 * @param {string|number} bookingId - Unique identifier for the booking
 * @param {number} holdingCardTime - Duration in seconds (remaining time)
 * @returns {Object} { remainingTime, startTimer, resetTimer }
 */
const useBookingTimer = (bookingId, holdingCardTime) => {
  const [remainingTime, setRemainingTime] = useState(0);
  const intervalRef = useRef(null);
  const hasExpiredRef = useRef(false);
  const holdingCardTimeRef = useRef(holdingCardTime);

  // Update ref when holdingCardTime changes
  useEffect(() => {
    holdingCardTimeRef.current = holdingCardTime;
  }, [holdingCardTime]);

  // Initialize timer when bookingId or holdingCardTime changes
  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (!bookingId || !holdingCardTime || holdingCardTime <= 0) {
      setRemainingTime(0);
      hasExpiredRef.current = false;
      return;
    }

    // Set initial remaining time from holdingCardTime
    const initialRemaining = Math.max(0, Math.floor(holdingCardTime));
    
    if (initialRemaining > 0) {
      setRemainingTime(initialRemaining);
      hasExpiredRef.current = false;

      // Start countdown - decrement every second
      intervalRef.current = setInterval(() => {
        setRemainingTime((prevTime) => {
          const newTime = Math.max(0, prevTime - 1);
          
          if (newTime <= 0) {
            // Timer expired
            if (intervalRef.current) {
              clearInterval(intervalRef.current);
              intervalRef.current = null;
            }
            hasExpiredRef.current = true;
          }
          
          return newTime;
        });
      }, 1000);
    } else {
      // Timer already expired
      setRemainingTime(0);
      hasExpiredRef.current = true;
    }

    // Cleanup on unmount or when dependencies change
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [bookingId, holdingCardTime]);

  // Reset/clear the timer
  const resetTimer = () => {
    // Clear interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Reset state and flags
    setRemainingTime(0);
    hasExpiredRef.current = false;
  };

  // Start the timer using holdingCardTime (for manual control if needed)
  const startTimer = (duration = holdingCardTime) => {
    if (!bookingId || !duration || duration <= 0) return;

    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Reset flags
    hasExpiredRef.current = false;

    // Set initial remaining time
    const initialRemaining = Math.max(0, Math.floor(duration));
    if (initialRemaining <= 0) {
      setRemainingTime(0);
      hasExpiredRef.current = true;
      return;
    }

    setRemainingTime(initialRemaining);

    // Start countdown interval
    intervalRef.current = setInterval(() => {
      setRemainingTime((prevTime) => {
        const newTime = Math.max(0, prevTime - 1);
        
        if (newTime <= 0) {
          // Timer expired
          if (intervalRef.current) {
            clearInterval(intervalRef.current);
            intervalRef.current = null;
          }
          hasExpiredRef.current = true;
        }
        
        return newTime;
      });
    }, 1000);
  };

  return {
    remainingTime,
    startTimer,
    resetTimer,
  };
};

export default useBookingTimer;

